import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Calendar,
  Target,
  Award,
  Clock,
  BarChart3,
  Activity,
  Zap,
  CheckCircle,
  Star,
  Flame,
  Trophy
} from 'lucide-react';
import { TechCard, TechBadge } from './TechUI';
import { careerAPI } from '../utils/api';
import { toast } from 'react-hot-toast';

const AnalyticsDashboard = ({ userId }) => {
  const [analytics, setAnalytics] = useState({
    totalLearningHours: 0,
    currentStreak: 0,
    longestStreak: 0,
    completedTasks: 0,
    totalTasks: 0,
    skillsGained: 0,
    projectsCompleted: 0,
    weeklyProgress: [0, 0, 0, 0, 0, 0, 0],
    skillProgress: [],
    recentAchievements: [],
    learningVelocity: 'Low',
    estimatedCompletion: 'N/A',
    careerReadiness: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await careerAPI.getUserAnalytics();
        const analyticsData = response.analytics || response;
        
        // Transform API data to match component structure
        const userStats = analyticsData.userStats || {};
        const overallProgress = analyticsData.overallProgress || 0;
        
        // Calculate weekly progress (mock for now, can be enhanced with real data)
        const weeklyProgress = [0, 0, 0, 0, 0, 0, 0];
        
        // Transform achievements
        const recentAchievements = (analyticsData.recentAchievements || []).map(achievement => {
          const iconMap = {
            'week': Flame,
            'project': Trophy,
            'skill': Star,
            'tasks': Award
          };
          
          // Determine icon based on achievement type
          let Icon = Trophy;
          for (const [key, icon] of Object.entries(iconMap)) {
            if (achievement.type?.toLowerCase().includes(key)) {
              Icon = icon;
              break;
            }
          }
          
          // Format date
          let dateStr = 'Recently';
          if (achievement.unlockedAt) {
            const date = achievement.unlockedAt.toDate ? achievement.unlockedAt.toDate() : new Date(achievement.unlockedAt);
            const daysAgo = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
            if (daysAgo === 0) dateStr = 'Today';
            else if (daysAgo === 1) dateStr = '1 day ago';
            else if (daysAgo < 7) dateStr = `${daysAgo} days ago`;
            else if (daysAgo < 30) dateStr = `${Math.floor(daysAgo / 7)} weeks ago`;
            else dateStr = `${Math.floor(daysAgo / 30)} months ago`;
          }
          
          return {
            title: achievement.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Achievement',
            description: achievement.metadata?.description || 'Great job!',
            date: dateStr,
            icon: Icon
          };
        });
        
        setAnalytics({
          totalLearningHours: Math.floor((userStats.tasksCompleted || 0) * 0.5), // Estimate: 30 min per task
          currentStreak: userStats.currentStreak || 0,
          longestStreak: userStats.longestStreak || 0,
          completedTasks: userStats.tasksCompleted || 0,
          totalTasks: analyticsData.totalTasks || 0,
          skillsGained: Math.floor((userStats.tasksCompleted || 0) / 10), // Estimate
          projectsCompleted: 0, // Can be enhanced with real project data
          weeklyProgress: weeklyProgress,
          skillProgress: [], // Can be enhanced with real skill data
          recentAchievements: recentAchievements.slice(0, 3),
          learningVelocity: overallProgress > 70 ? 'High' : overallProgress > 40 ? 'Medium' : 'Low',
          estimatedCompletion: 'N/A', // Can be calculated from roadmap data
          careerReadiness: Math.min(overallProgress, 100)
        });
      } catch (error) {
        console.error('Error fetching analytics:', error);
        toast.error('Failed to load analytics data');
        // Keep default/empty state on error
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [userId]);

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-emerald-500';
    if (progress >= 60) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'advanced':
        return 'text-emerald-400 bg-emerald-900/30 border-emerald-700';
      case 'intermediate':
        return 'text-yellow-400 bg-yellow-900/30 border-yellow-700';
      case 'beginner':
        return 'text-blue-400 bg-blue-900/30 border-blue-700';
      default:
        return 'text-slate-400 bg-slate-900/30 border-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-white font-semibold">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-white mb-2">Your Learning Analytics</h2>
        <p className="text-slate-400">Track your progress, streaks, and skill development over time</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <TechCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-emerald-500/20 rounded-full p-3">
              <Clock className="w-6 h-6 text-emerald-400" />
            </div>
            <TechBadge type="success">+5h this week</TechBadge>
          </div>
          <p className="text-slate-400 text-sm mb-1">Total Learning Hours</p>
          <p className="text-4xl font-bold text-white">{analytics.totalLearningHours}</p>
        </TechCard>

        <TechCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-orange-500/20 rounded-full p-3">
              <Flame className="w-6 h-6 text-orange-400" />
            </div>
            <TechBadge type="warning">Best: {analytics.longestStreak}</TechBadge>
          </div>
          <p className="text-slate-400 text-sm mb-1">Current Streak</p>
          <p className="text-4xl font-bold text-white">{analytics.currentStreak} days</p>
        </TechCard>

        <TechCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-blue-500/20 rounded-full p-3">
              <Target className="w-6 h-6 text-blue-400" />
            </div>
            <TechBadge>{Math.round((analytics.completedTasks / analytics.totalTasks) * 100)}%</TechBadge>
          </div>
          <p className="text-slate-400 text-sm mb-1">Tasks Completed</p>
          <p className="text-4xl font-bold text-white">{analytics.completedTasks}/{analytics.totalTasks}</p>
        </TechCard>

        <TechCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-purple-500/20 rounded-full p-3">
              <Award className="w-6 h-6 text-purple-400" />
            </div>
            <TechBadge type="primary">+2 this month</TechBadge>
          </div>
          <p className="text-slate-400 text-sm mb-1">Skills Gained</p>
          <p className="text-4xl font-bold text-white">{analytics.skillsGained}</p>
        </TechCard>
      </div>

      {/* Weekly Progress Chart */}
      <TechCard className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">Weekly Progress</h3>
            <p className="text-slate-400">Your completion rate over the last 7 days</p>
          </div>
          <Activity className="w-8 h-8 text-emerald-400" />
        </div>
        
        <div className="flex items-end justify-between space-x-2 h-64">
          {analytics.weeklyProgress.map((value, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div className="w-full bg-slate-800 rounded-t-lg relative overflow-hidden" style={{ height: '100%' }}>
                <div
                  className="absolute bottom-0 w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t-lg transition-all duration-500"
                  style={{ height: `${value}%` }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{value}%</span>
                </div>
              </div>
              <p className="text-slate-500 text-xs mt-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][index]}
              </p>
            </div>
          ))}
        </div>
      </TechCard>

      {/* Skill Progress */}
      <TechCard className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">Skill Development</h3>
            <p className="text-slate-400">Your progress in key skills for your target role</p>
          </div>
          <BarChart3 className="w-8 h-8 text-emerald-400" />
        </div>

        <div className="space-y-6">
          {analytics.skillProgress.map((skill, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <span className="text-white font-semibold">{skill.name}</span>
                  <div className={`px-2 py-1 rounded-lg border text-xs ${getLevelColor(skill.level)}`}>
                    {skill.level}
                  </div>
                </div>
                <span className="text-slate-400 font-mono">{skill.progress}%</span>
              </div>
              <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className={`h-full ${getProgressColor(skill.progress)} transition-all duration-500`}
                  style={{ width: `${skill.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </TechCard>

      {/* Career Readiness Score */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TechCard className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Career Readiness</h3>
              <p className="text-slate-400">How prepared you are for your target role</p>
            </div>
            <TrendingUp className="w-8 h-8 text-emerald-400" />
          </div>

          <div className="flex items-center justify-center mb-6">
            <div className="relative w-48 h-48">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle
                  className="text-slate-700"
                  strokeWidth="8"
                  stroke="currentColor"
                  fill="transparent"
                  r="40"
                  cx="50"
                  cy="50"
                />
                <circle
                  className="text-emerald-500"
                  strokeWidth="8"
                  strokeDasharray={40 * 2 * Math.PI}
                  strokeDashoffset={40 * 2 * Math.PI - (analytics.careerReadiness / 100) * (40 * 2 * Math.PI)}
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="transparent"
                  r="40"
                  cx="50"
                  cy="50"
                  style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-5xl font-bold text-white">{analytics.careerReadiness}</span>
                <span className="text-slate-400 text-sm mt-1">/ 100</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <Zap className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="text-slate-400 text-sm mb-1">Learning Velocity</p>
              <p className="text-white font-bold">{analytics.learningVelocity}</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <Calendar className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-slate-400 text-sm mb-1">Est. Completion</p>
              <p className="text-white font-bold">{analytics.estimatedCompletion}</p>
            </div>
          </div>
        </TechCard>

        <TechCard className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Recent Achievements</h3>
              <p className="text-slate-400">Milestones you've unlocked</p>
            </div>
            <Trophy className="w-8 h-8 text-yellow-400" />
          </div>

          <div className="space-y-4">
            {analytics.recentAchievements.map((achievement, index) => {
              const IconComponent = achievement.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 flex items-center space-x-4 hover:border-emerald-500/50 transition-all"
                >
                  <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full p-3 flex-shrink-0">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-grow">
                    <h4 className="text-white font-semibold mb-1">{achievement.title}</h4>
                    <p className="text-slate-400 text-sm">{achievement.description}</p>
                  </div>
                  <span className="text-slate-500 text-xs whitespace-nowrap">{achievement.date}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-center">
            <p className="text-slate-500 text-sm">
              🎯 Complete more tasks to unlock new achievements!
            </p>
          </div>
        </TechCard>
      </div>

      {/* Monthly Summary */}
      <TechCard className="p-8 bg-gradient-to-br from-emerald-900/20 to-teal-900/20 border-emerald-700/50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">📊 Monthly Progress Report</h3>
            <p className="text-slate-300 mb-4">
              You're doing great! Keep up the momentum and you'll reach your career goals in no time.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span className="text-white"><span className="font-bold">{analytics.projectsCompleted}</span> projects completed</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span className="text-white"><span className="font-bold">{analytics.skillsGained}</span> new skills gained</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span className="text-white"><span className="font-bold">{analytics.totalLearningHours}h</span> of learning time</span>
              </div>
            </div>
          </div>
        </div>
      </TechCard>
    </div>
  );
};

export default AnalyticsDashboard;

